import React, { useState, useEffect, useContext, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faHospital,
  faUserMd,
  faFileMedical,
  faStethoscope,
  faChartLine,
  faSyncAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Line } from "react-chartjs-2";
import axios from "axios";
import { Chart } from "chart.js";
import {
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { RateLimitAlertContext } from "../contexts/RateLimitAlertContext";
import { AdminAccessAlertContext } from "../contexts/AdminAccessAlertContext";
import { BASE_URL } from "../config";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

Chart.register(
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

const DashboardHome_v2 = () => {
  const [metrics, setMetrics] = useState({});
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeFrame, setTimeFrame] = useState("31 DAY");
  const { openAlert: openRateLimitAlert } = useContext(RateLimitAlertContext);
  const { openAlert: openAdminAccessAlert } = useContext(
    AdminAccessAlertContext
  );
  const navigate = useNavigate();

  const fetchData = useCallback(
    async (period) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authentication/token");
        const [metricsRes, statsRes] = await Promise.all([
          axios.get(`${BASE_URL}/dashboard/metrics`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
          axios.get(`${BASE_URL}/dashboard/request-stats`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            params: { timeFrame: period },
          }),
        ]);
        setMetrics(metricsRes.data);
        prepareChartData(statsRes.data, period);
      } catch (error) {
        if (error.response?.status === 401) window.location.href = "/login";
        if (error.response?.status === 403) openAdminAccessAlert();
        if (error.response?.status === 429) openRateLimitAlert();
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    },
    [openAdminAccessAlert, openRateLimitAlert]
  );

  useEffect(() => {
    fetchData(timeFrame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFrame]);

  const formatNumber = (num) =>
    typeof num === "number" ? num.toLocaleString("en-US") : "0";

  const prepareChartData = (stats, period) => {
    const days = parseInt(period.replace(" DAY", ""));
    const labels = [];
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days + 1);
    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      labels.push(
        new Date(d).toLocaleDateString("en-PH", {
          month: "short",
          day: "numeric",
        })
      );
    }
    const loaCounts = Array(labels.length).fill(0);
    stats.forEach((item) => {
      const dateIndex = labels.indexOf(
        new Date(item.date).toLocaleDateString("en-PH", {
          month: "short",
          day: "numeric",
        })
      );
      if (dateIndex !== -1) {
        loaCounts[dateIndex] = item.loa_count;
      }
    });
    setChartData({
      labels,
      datasets: [
        {
          label: "Weekly LOA",
          data: loaCounts,
          borderColor: "#10b981",
          backgroundColor: "rgba(16, 185, 129, 0.12)",
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 6,
          pointBackgroundColor: "#ffffff",
          pointBorderColor: "#10b981",
          fill: true,
        },
      ],
    });
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: "#334155",
          font: {
            size: 12,
            family:
              "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          },
          padding: 12,
        },
      },
      tooltip: {
        backgroundColor: "#0f172a",
        titleColor: "#e2e8f0",
        bodyColor: "#f8fafc",
        padding: 10,
        titleFont: {
          size: 12,
          family:
            "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        },
        bodyFont: {
          size: 12,
          family:
            "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        },
        displayColors: true,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: "#64748b",
          font: {
            size: 11,
            family:
              "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          },
          maxRotation: 0,
          autoSkip: true,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#e2e8f0" },
        ticks: {
          color: "#64748b",
          font: {
            size: 11,
            family:
              "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          },
          stepSize: 1,
        },
      },
    },
    elements: {
      line: {
        borderWidth: 3,
      },
      point: {
        radius: 4,
        hoverRadius: 7,
      },
    },
    hover: {
      mode: "nearest",
      intersect: true,
    },
  };

  const cardData = [
    {
      icon: faUsers,
      title: "Active Members",
      gradient: "from-sky-500 via-cyan-400 to-blue-500",
      path: "/members",
    },
    {
      icon: faHospital,
      title: "Accredited Provider",
      gradient: "from-emerald-500 via-teal-400 to-green-500",
      path: "/medical/provider",
    },
    {
      icon: faUserMd,
      title: "Active Physician",
      gradient: "from-indigo-500 via-violet-400 to-purple-500",
      path: "/physician",
    },
    {
      icon: faFileMedical,
      title: "Weekly Loa",
      gradient: "from-rose-500 via-pink-400 to-red-500",
      path: "/dashboard",
    },
    {
      icon: faStethoscope,
      title: "Physicians With Affiliations",
      gradient: "from-amber-500 via-orange-400 to-yellow-500",
      path: "/physician",
    },
  ];

  const LoadingSkeleton = () => (
    <div className="animate-pulse space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="h-24 rounded-2xl bg-white/60 backdrop-blur border border-slate-200"
          />
        ))}
      </div>
      <div className="h-72 rounded-2xl bg-white/60 backdrop-blur border border-slate-200" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-emerald-50 p-4 md:p-6">
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      <div className="pointer-events-none fixed inset-0 -z-10 opacity-40">
        <div className="absolute -top-32 -right-32 h-96 w-96 bg-gradient-to-br from-blue-200 to-cyan-200 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 bg-gradient-to-br from-emerald-200 to-teal-200 rounded-full blur-3xl animate-pulse-slow" />
      </div>
      <div className="max-w-1xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/80 rounded-2xl shadow-sm border border-slate-200 transition-all hover:shadow-md">
              <FontAwesomeIcon
                icon={faChartLine}
                className="text-emerald-600 text-lg"
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
                  {/* AllAccess System Web Portal{" "} */}
                  All Access System{" "}
                  <span className="text-sm font-normal text-emerald-600">
                    (Version 2.0)
                  </span>
                </h1>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs font-medium border border-emerald-100">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Hospital & HMO Operations Dashboard • Real-time insights
              </p>
            </div>
          </div>
          <button
            onClick={() => fetchData(timeFrame)}
            disabled={loading}
            aria-busy={loading}
            className={clsx(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium shadow-sm border transition-all duration-300 hover:scale-105",
              loading
                ? "bg-slate-100 text-slate-500 border-slate-200"
                : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300 hover:shadow-md"
            )}
          >
            <FontAwesomeIcon
              icon={faSyncAlt}
              className={clsx(
                "text-xs",
                loading ? "animate-spin text-slate-400" : "text-emerald-600"
              )}
            />
            {loading ? "Refreshing..." : "Refresh Data"}
          </button>
        </div>
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cardData.map((card, index) => (
                <div
                  key={index}
                  className={clsx(
                    "group relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105 cursor-pointer",
                    "bg-gradient-to-br bg-[length:200%_200%] bg-left-top hover:bg-right-bottom duration-500",
                    card.gradient
                  )}
                  onClick={() => navigate(card.path)}
                >
                  <div className="absolute left-0 top-0 bottom-0 w-20 flex items-center justify-center overflow-hidden">
                    <FontAwesomeIcon
                      icon={card.icon}
                      className="text-6xl text-white opacity-20 transition-all duration-300 group-hover:opacity-30 group-hover:scale-110"
                    />
                  </div>
                  <div className="pl-24 p-5 relative z-10">
                    <h3 className="text-sm font-medium text-white/90">
                      {card.title}
                    </h3>
                    <p className="text-2xl font-bold mt-1 text-white">
                      {formatNumber(metrics[card.title])}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-md p-6 border border-slate-200 transition-all hover:shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 transition-all hover:scale-110">
                    <FontAwesomeIcon icon={faChartLine} className="text-base" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-800">
                    LOA Trends for Last {timeFrame.replace(" DAY", " Days")}
                  </h2>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTimeFrame("7 DAY")}
                    className={clsx(
                      "px-3.5 py-2 text-sm font-medium rounded-lg transition-all duration-300 border hover:scale-105",
                      timeFrame === "7 DAY"
                        ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300 hover:shadow-md"
                    )}
                  >
                    7 Days
                  </button>
                  <button
                    onClick={() => setTimeFrame("31 DAY")}
                    className={clsx(
                      "px-3.5 py-2 text-sm font-medium rounded-lg transition-all duration-300 border hover:scale-105",
                      timeFrame === "31 DAY"
                        ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300 hover:shadow-md"
                    )}
                  >
                    1 Month
                  </button>
                </div>
              </div>
              <div className="h-72">
                {chartData ? (
                  <Line data={chartData} options={chartOptions} />
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-500">
                    No data available
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardHome_v2;